/*
 * Seafood Factory — territory / Shopify Location controller.
 *
 * Persistence model:
 *   - Source of truth: Shopify cart attributes (readable in Liquid as
 *     cart.attributes.location_slug etc.). Written via POST /cart/update.js.
 *   - Client cache: localStorage.sf_location / sf_territory (instant JS read;
 *     silently reconciles into the cart attribute if the cart was reset).
 *
 * Public API on window.SFTerritory:
 *   current       -> {territory_id, location_slug, ...} | null
 *   territories   -> full list from snippets/territory-data.liquid
 *   locations     -> full list from snippets/territory-data.liquid
 *   availability  -> {product_id: bool} for products rendered on this page
 *   isProductOpen(productId) -> bool
 *   open()        -> show gate (first-visit / no selection mode)
 *   change()      -> show gate (change mode)
 *   select(id)    -> Promise<void>, saves selection + reloads
 */
(function () {
  'use strict';

  const LS_LOCATION = 'sf_location';
  const LS_TERRITORY = 'sf_territory';
  const LS_LANG = 'sf_gate_lang';

  const STRINGS = {
    en: {
      title: 'Choose your delivery area',
      search: 'Search delivery areas…',
      empty: 'No matches. Try another spelling.',
      close: 'Close',
      changeArea: 'Change delivery area',
      unavailable: 'Not available in your selected delivery area.',
      cartLineWarning: 'This item is not available in your selected delivery area.',
      cartBlocked: 'Some items in your cart are not available for this delivery area. Remove them or change area to continue.',
      toastBlocked: 'This product is not available in your delivery area.',
      changeShort: 'Change area',
    },
    ar: {
      title: 'اختر منطقة التوصيل',
      search: 'ابحث عن منطقة التوصيل…',
      empty: 'لا توجد نتائج. جرّب كتابة مختلفة.',
      close: 'إغلاق',
      changeArea: 'تغيير منطقة التوصيل',
      unavailable: 'غير متاح في منطقة التوصيل المحددة.',
      cartLineWarning: 'هذا المنتج غير متاح في منطقة التوصيل المحددة.',
      cartBlocked: 'بعض المنتجات في السلة غير متاحة لهذه المنطقة. احذفها أو غيّر المنطقة للمتابعة.',
      toastBlocked: 'هذا المنتج غير متاح في منطقة التوصيل المحددة.',
      changeShort: 'تغيير المنطقة',
    },
  };

  function readDataset() {
    const node = document.getElementById('sf-territory-data');
    if (!node) return { locations: [], territories: [] };
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      console.error('[sf-territory] bad dataset json', e);
      return { locations: [], territories: [] };
    }
  }

  function readAvailabilityMap() {
    const node = document.getElementById('sf-availability-map');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return {};
    }
  }

  function readCurrentFromBody() {
    const b = document.body.dataset;
    // Launch state: a valid saved selection must have a resolved
    // location_slug (i.e. an Active territory). Any lingering legacy state
    // without a location_slug — including stale "Out of Zone" cart attrs
    // from a prior pre-launch session — is treated as no-selection and the
    // boot path will clear it + open the gate.
    if (!b.locationSlug) return null;
    return {
      territory_id: b.territoryId || '',
      territory_ar: b.territoryAr || '',
      territory_en: b.territoryEn || '',
      location_slug: b.locationSlug,
      location_name: b.locationName || '',
      service_status: b.serviceStatus || 'Active',
    };
  }

  function initialLang() {
    const stored = localStorage.getItem(LS_LANG);
    if (stored === 'ar' || stored === 'en') return stored;
    const htmlLang = (document.documentElement.getAttribute('lang') || '').toLowerCase();
    if (htmlLang.startsWith('ar')) return 'ar';
    const dir = (document.documentElement.getAttribute('dir') || '').toLowerCase();
    if (dir === 'rtl') return 'ar';
    return 'en';
  }

  function t(key) {
    const lang = SF.lang || 'en';
    return (STRINGS[lang] && STRINGS[lang][key]) || STRINGS.en[key] || key;
  }

  function debounce(fn, wait) {
    let timer;
    return function () {
      const ctx = this, args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  // ---------- Persistence ----------

  async function writeToCart(territory) {
    const attributes = {
      territory_id: territory.id,
      territory_ar: territory.name_ar,
      territory_en: territory.name_en,
      location_slug: territory.location_slug || '',
      location_name: territory.location_name || '',
      // service_status is written for forward-compat / telemetry; the
      // launch dataset only exposes Active territories so this is always
      // "Active" today. Kept so future out-of-zone work has a hook.
      service_status: territory.service_status || 'Active',
    };
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
    });
    if (!res.ok) {
      throw new Error('Cart update failed: HTTP ' + res.status);
    }
    localStorage.setItem(LS_LOCATION, territory.location_slug || '');
    localStorage.setItem(LS_TERRITORY, territory.id);
  }

  async function clearCartTerritory() {
    const attributes = {
      territory_id: '',
      territory_ar: '',
      territory_en: '',
      location_slug: '',
      location_name: '',
      service_status: '',
    };
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: attributes }),
      });
    } catch (e) { /* best-effort */ }
    localStorage.removeItem(LS_LOCATION);
    localStorage.removeItem(LS_TERRITORY);
  }

  function findTerritoryById(id) {
    if (!id) return null;
    return SF.dataset.territories.find(function (t) { return t.id === id; }) || null;
  }

  function findLocation(slug) {
    return SF.dataset.locations.find(function (l) { return l.slug === slug; }) || null;
  }

  function enrichTerritory(t) {
    if (!t) return null;
    const loc = t.location_slug ? findLocation(t.location_slug) : null;
    return Object.assign({}, t, {
      location_name: loc ? loc.name_en : '',
      location_name_ar: loc ? loc.name_ar : '',
      service_status: t.service_status || 'Active',
    });
  }

  // ---------- Gate UI ----------

  function buildGate() {
    let el = document.getElementById('SFTerritoryGate');
    if (!el) return null;
    if (el.dataset.built === '1') return el;

    const lang = SF.lang;
    el.setAttribute('data-lang', lang);
    el.innerHTML =
      '<div class="sf-gate__panel" role="dialog" aria-modal="true" aria-labelledby="SFGateTitle">' +
        '<div class="sf-gate__header">' +
          '<h2 id="SFGateTitle" class="sf-gate__title"></h2>' +
          '<div class="sf-gate__header-actions">' +
            '<div class="sf-gate__lang" role="group" aria-label="Language">' +
              '<button type="button" data-lang="en" aria-pressed="' + (lang === 'en') + '">EN</button>' +
              '<button type="button" data-lang="ar" aria-pressed="' + (lang === 'ar') + '">ع</button>' +
            '</div>' +
            '<button type="button" class="sf-gate__close" aria-label="Close" hidden>×</button>' +
          '</div>' +
        '</div>' +
        '<div class="sf-gate__search">' +
          '<input type="search" autocomplete="off" spellcheck="false" />' +
        '</div>' +
        '<div class="sf-gate__body" role="listbox"></div>' +
      '</div>';

    el.dataset.built = '1';
    wireGate(el);
    renderGateStrings(el);
    renderList(el, '');
    return el;
  }

  function renderGateStrings(el) {
    el.querySelector('.sf-gate__title').textContent = t('title');
    el.querySelector('.sf-gate__search input').placeholder = t('search');
    el.querySelector('.sf-gate__close').setAttribute('aria-label', t('close'));
    el.setAttribute('data-lang', SF.lang);
    el.querySelectorAll('.sf-gate__lang button').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.dataset.lang === SF.lang));
    });
  }

  function renderList(el, query) {
    const body = el.querySelector('.sf-gate__body');
    const lang = SF.lang;
    const q = (query || '').trim().toLowerCase();

    // Filter: match against both AR and EN internally so a typo in either
    // language still finds the territory.
    const filtered = SF.dataset.territories.filter(function (tr) {
      if (!q) return true;
      return (tr.name_ar || '').toLowerCase().includes(q)
          || (tr.name_en || '').toLowerCase().includes(q);
    });

    if (!filtered.length) {
      body.innerHTML = '<div class="sf-gate__empty">' + t('empty') + '</div>';
      return;
    }

    // Alphabetize by the current language so the flat list scans nicely.
    const collator = new Intl.Collator(lang === 'ar' ? 'ar' : 'en', { sensitivity: 'base' });
    const sorted = filtered.slice().sort(function (a, b) {
      const av = lang === 'ar' ? a.name_ar : a.name_en;
      const bv = lang === 'ar' ? b.name_ar : b.name_en;
      return collator.compare(av || '', bv || '');
    });

    const current = SF.current ? SF.current.territory_id : '';
    let html = '';
    sorted.forEach(function (tr) {
      const label = escapeHtml(lang === 'ar' ? tr.name_ar : tr.name_en);
      const isCurrent = current === tr.id ? ' is-current' : '';
      html += '<button type="button" class="sf-gate__item' + isCurrent
           + '" role="option"'
           + ' aria-selected="' + (current === tr.id ? 'true' : 'false') + '"'
           + ' data-territory-id="' + escapeHtml(tr.id) + '">'
           + '<span class="sf-gate__item-label">' + label + '</span>'
           + '</button>';
    });
    body.innerHTML = html;
    body.scrollTop = 0;
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (m) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m];
    });
  }

  function wireGate(el) {
    // language toggle
    el.querySelectorAll('.sf-gate__lang button').forEach(function (b) {
      b.addEventListener('click', function () {
        SF.lang = b.dataset.lang;
        localStorage.setItem(LS_LANG, SF.lang);
        renderGateStrings(el);
        renderList(el, el.querySelector('.sf-gate__search input').value);
      });
    });

    // close (only visible in change-mode)
    el.querySelector('.sf-gate__close').addEventListener('click', function () {
      closeGate();
    });

    // search
    const input = el.querySelector('.sf-gate__search input');
    input.addEventListener('input', debounce(function () {
      renderList(el, input.value);
    }, 100));

    // pick territory
    el.querySelector('.sf-gate__body').addEventListener('click', function (ev) {
      const btn = ev.target.closest('[data-territory-id]');
      if (!btn) return;
      SF.select(btn.dataset.territoryId).catch(function (err) {
        console.error('[sf-territory] select failed', err);
      });
    });
  }

  function openGate(mode) {
    const el = buildGate();
    if (!el) return;
    el.classList.add('is-open');
    el.querySelector('.sf-gate__close').hidden = (mode !== 'change');
    document.documentElement.classList.add('sf-gate-locked');
    // Reset scroll + search state each time the gate opens so nothing carries over
    const input = el.querySelector('.sf-gate__search input');
    if (input) input.value = '';
    renderList(el, '');
    setTimeout(function () { if (input) input.focus(); }, 30);
  }

  function closeGate() {
    const el = document.getElementById('SFTerritoryGate');
    if (!el) return;
    el.classList.remove('is-open');
    document.documentElement.classList.remove('sf-gate-locked');
  }

  // ---------- Toast ----------
  function toast(msg) {
    let el = document.querySelector('.sf-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'sf-toast';
      document.body.appendChild(el);
    }
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { el.classList.remove('is-visible'); }, 2600);
  }

  // ---------- Cart-add veto ----------
  function isProductOpen(productId) {
    if (!SF.current) return true; // nothing chosen yet
    if (!(productId in SF.availability)) return true; // unknown = allow
    return SF.availability[productId] !== false;
  }

  function hookFetch() {
    if (!window.fetch) return;
    const orig = window.fetch;
    window.fetch = function (input, init) {
      try {
        const url = (typeof input === 'string') ? input : (input && input.url) || '';
        if (/\/cart\/add(\.js)?$/.test(url.split('?')[0])) {
          const productId = extractProductId(init);
          if (productId && !isProductOpen(productId)) {
            toast(t('toastBlocked'));
            return Promise.reject(new Error('sf: product closed at current location'));
          }
        }
      } catch (e) { /* fall through */ }
      return orig.apply(this, arguments);
    };
  }

  function extractProductId(init) {
    if (!init || !init.body) return null;
    try {
      let body = init.body;
      if (body instanceof FormData) {
        const id = body.get('id');
        return findProductIdForVariant(id);
      }
      if (typeof body === 'string') {
        // try JSON
        if (body.trim().startsWith('{')) {
          const parsed = JSON.parse(body);
          if (parsed.items && parsed.items.length) {
            return findProductIdForVariant(parsed.items[0].id) || parsed.items[0].product_id;
          }
          if (parsed.id) return findProductIdForVariant(parsed.id);
        }
        // urlencoded
        const params = new URLSearchParams(body);
        return findProductIdForVariant(params.get('id'));
      }
    } catch (e) { return null; }
    return null;
  }

  function findProductIdForVariant(variantId) {
    if (!variantId) return null;
    // look up cached DOM data attribute if present
    const node = document.querySelector('[data-variant-id="' + variantId + '"][data-product-id]');
    if (node) return Number(node.dataset.productId);
    return null;
  }

  // Also intercept forms that submit via classic multipart to /cart/add
  function hookForms() {
    document.addEventListener('submit', function (ev) {
      const form = ev.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = (form.getAttribute('action') || '').split('?')[0];
      if (!/\/cart\/add$/.test(action) && form.dataset.type !== 'add-to-cart-form') return;

      // Prefer the explicit server-rendered hint if present (quickview / PDP wrappers).
      const hintNode = form.closest('[data-sf-open]');
      if (hintNode && hintNode.dataset.sfOpen === 'false') {
        ev.preventDefault();
        ev.stopPropagation();
        toast(t('toastBlocked'));
        return;
      }

      const idInput = form.querySelector('[name="id"]');
      const variantId = idInput && idInput.value;
      const productId = findProductIdForVariant(variantId)
        || Number(form.closest('[data-product-id]') && form.closest('[data-product-id]').dataset.productId)
        || Number(hintNode && hintNode.dataset.sfProductId);
      if (productId && !isProductOpen(productId)) {
        ev.preventDefault();
        ev.stopPropagation();
        toast(t('toastBlocked'));
      }
    }, true);
  }

  // ---------- Cart page: disable checkout when any line is closed ----------
  function guardCheckoutButtons() {
    const closedLines = document.querySelectorAll('[data-sf-line-closed="1"]');
    if (!closedLines.length) return;
    document.querySelectorAll('.cart__checkout, button[name="checkout"]').forEach(function (btn) {
      btn.setAttribute('disabled', 'disabled');
      btn.classList.add('sf-checkout-blocked');
    });
    // notice
    document.querySelectorAll('[data-sf-cart-notice]').forEach(function (n) {
      n.textContent = t('cartBlocked');
      n.hidden = false;
    });
  }

  // ---------- Bootstrap ----------
  const SF = {
    dataset: readDataset(),
    availability: readAvailabilityMap(),
    lang: initialLang(),
    current: null,
    isProductOpen: isProductOpen,
    open: function () { openGate('initial'); },
    change: function () { openGate('change'); },
    select: async function (territoryId) {
      const raw = findTerritoryById(territoryId);
      if (!raw) throw new Error('unknown territory: ' + territoryId);
      const t = enrichTerritory(raw);
      await writeToCart(t);
      // full reload so server-rendered filtering picks up the new location
      window.location.reload();
    },
  };

  window.SFTerritory = SF;

  function boot() {
    SF.current = readCurrentFromBody();

    // Stale-territory reconciliation: if the cart attribute names a territory
    // that no longer exists in the launch-filtered dataset (admin deactivated
    // it, renamed its handle, or flipped it to Out of Zone), wipe cart attrs
    // and open the gate. Covers the pre-launch session that may still carry
    // service_status=Out-of-Zone cart attrs from an earlier dev-theme visit.
    if (SF.current && SF.current.territory_id) {
      const match = findTerritoryById(SF.current.territory_id);
      if (!match) {
        SF.current = null;
        clearCartTerritory().finally(function () { openGate('initial'); });
        // continue booting hooks so the gate is interactive
        hookFetch(); hookForms(); wireChangeAreaDelegation();
        return;
      }
      // Keep location_slug in sync if the admin moved the territory to a
      // different warehouse between page loads.
      if ((match.location_slug || '') !== (SF.current.location_slug || '')) {
        const enriched = enrichTerritory(match);
        writeToCart(enriched).catch(function () { /* best-effort */ });
        SF.current.location_slug = enriched.location_slug || '';
        SF.current.location_name = enriched.location_name || '';
      }
    }

    // Reconciliation: cache says one thing, cart attribute says nothing
    if (!SF.current) {
      const cached = localStorage.getItem(LS_TERRITORY);
      if (cached) {
        const t = enrichTerritory(findTerritoryById(cached));
        if (t) {
          writeToCart(t).then(function () {
            SF.current = { territory_id: t.id, territory_ar: t.name_ar, territory_en: t.name_en,
                           location_slug: t.location_slug || '', location_name: t.location_name,
                           service_status: t.service_status };
          }).catch(function () { /* ignore */ });
        }
      }
      openGate('initial');
    }

    // Hook AJAX cart-adds regardless (also guards after a territory change)
    hookFetch();
    hookForms();
    guardCheckoutButtons();
    wireChangeAreaDelegation();
  }

  function wireChangeAreaDelegation() {
    // Delegated so dynamically-injected [data-sf-change-area] controls (e.g.
    // the OoZ banner button) are picked up without re-wiring.
    if (document._sfChangeAreaWired) return;
    document._sfChangeAreaWired = true;
    document.addEventListener('click', function (ev) {
      const target = ev.target && ev.target.closest && ev.target.closest('[data-sf-change-area]');
      if (!target) return;
      ev.preventDefault();
      SF.change();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
